import { useMemo } from 'react';
import { checkPermission, resolveScope } from '@shared/lib/authorization';
import { useAuthStore } from '@shared/store/auth.store';
import type { PermissionScope } from '@shared/types/auth';

export interface PermissionApi {
  /** True when the backend marked this user as an owner (bypasses every check). */
  isOwner: boolean;
  /** Effective permission keys as returned by the backend. */
  permissions: readonly string[];
  /** False until the authorization payload has been applied (still bootstrapping). */
  isReady: boolean;
  /** Single check: `can('employees.create')`. */
  can: (permission: string) => boolean;
  /** True when **any** of the permissions is granted. */
  canAny: (permissions: readonly string[]) => boolean;
  /** True when **all** of the permissions are granted. */
  canAll: (permissions: readonly string[]) => boolean;
  /** Backend data scope for a permission, or null when not granted. */
  scopeFor: (permission: string) => PermissionScope | null;
  /** Convenience: does the permission carry one of the given scopes? */
  hasScope: (permission: string, scopes: readonly PermissionScope[]) => boolean;
}

/**
 * The single entry point for permission checks in React components.
 *
 * It subscribes to the authorization slice of the auth store, so every consumer
 * re-renders when the backend hands us a new permission set (login, refresh,
 * `/auth/me`, logout). Authorization itself is enforced by the backend — these
 * helpers only decide what is worth rendering.
 */
export function usePermissions(): PermissionApi {
  const authorization = useAuthStore((state) => state.authorization);

  return useMemo(() => {
    const can = (permission: string) =>
      checkPermission(authorization, authorization.permissionSet, permission);
    const scopeFor = (permission: string) => resolveScope(authorization, permission);
    return {
      isOwner: authorization.isOwner,
      permissions: authorization.permissions,
      isReady: authorization.loaded,
      can,
      canAny: (permissions) => permissions.some(can),
      canAll: (permissions) => permissions.every(can),
      scopeFor,
      hasScope: (permission, scopes) => {
        const scope = scopeFor(permission);
        return scope !== null && scopes.includes(scope);
      },
    };
  }, [authorization]);
}

/** Reactive single-permission check for call sites that need just one boolean. */
export function useCan(permission: string): boolean {
  return usePermissions().can(permission);
}

/** Reactive "any of" check. Pass a stable array (module constant or memoized). */
export function useCanAny(permissions: readonly string[]): boolean {
  return usePermissions().canAny(permissions);
}

/** Reactive "all of" check. Pass a stable array (module constant or memoized). */
export function useCanAll(permissions: readonly string[]): boolean {
  return usePermissions().canAll(permissions);
}

/** Reactive scope lookup for UI that adapts to the backend's data scope. */
export function useScope(permission: string): PermissionScope | null {
  return usePermissions().scopeFor(permission);
}
